<template>
    <v-sheet width="100%" class="ma-2 bg-transparent">
        <div class="d-flex align-center flex-wrap mb-4" style="gap: 12px;">
            <v-btn-toggle v-model="view" mandatory density="compact" color="primary" variant="outlined">
                <v-btn value="arcana">By Arcana</v-btn>
                <v-btn value="byBoss">By Boss</v-btn>
                <v-btn value="personalBest">Personal Bests</v-btn>
            </v-btn-toggle>

            <v-select
                v-model="selectedBoss"
                :items="bossOptions"
                item-title="label"
                item-value="raceId"
                label="Boss"
                density="compact"
                variant="outlined"
                hide-details
                style="max-width: 260px;"
                :disabled="bossOptions.length === 0">
            </v-select>

            <v-text-field
                v-model="nameFilter"
                label="Filter by player name"
                density="compact"
                variant="outlined"
                prepend-inner-icon="mdi-magnify"
                hide-details
                clearable
                style="max-width: 220px;">
            </v-text-field>

            <v-btn size="small" variant="text" prepend-icon="mdi-refresh" :loading="loading" @click="load">
                Refresh
            </v-btn>

            <v-spacer></v-spacer>

            <v-btn v-if="(currentUser?.role || 0) >= 3" size="small" variant="outlined"
                prepend-icon="mdi-database-import" @click="showImportDialog = true">
                Import Data
            </v-btn>
        </div>

        <v-dialog v-model="showImportDialog" max-width="480" @after-leave="resetImportForm">
            <v-card>
                <v-toolbar density="compact" color="transparent">
                    <v-toolbar-title class="text-subtitle-1">Import Leaderboard Data</v-toolbar-title>
                    <v-btn icon="mdi-close" variant="text" @click="showImportDialog = false"></v-btn>
                </v-toolbar>
                <v-card-text>
                    <div class="text-caption text-grey mb-4">
                        Upload another user's <code>dpslog.db</code> or <code>leaderboard.db</code> file. Only its
                        players/fights/fight_participants rows are extracted and merged in - nothing else in that
                        file (raw session logs, config, etc.) is read or kept, even if the file itself is very
                        large. If the file doesn't have that data yet (an older export, or one from an install
                        that hasn't run its own backfill), it's derived automatically from the file's raw
                        session logs before importing.
                    </div>

                    <v-file-input
                        v-model="importFile"
                        label="Database file"
                        accept=".db,.sqlite,.sqlite3"
                        density="compact"
                        variant="outlined"
                        prepend-icon=""
                        prepend-inner-icon="mdi-database"
                        hide-details
                        class="mb-4">
                    </v-file-input>

                    <v-text-field
                        v-model="importLabel"
                        label="Source label (optional)"
                        placeholder="e.g. a player's name"
                        density="compact"
                        variant="outlined"
                        hide-details
                        class="mb-4">
                    </v-text-field>

                    <div v-if="importing" class="mb-4">
                        <v-progress-linear
                            :model-value="importIndeterminate ? undefined : importProgress"
                            :indeterminate="importIndeterminate"
                            color="primary" height="6" rounded>
                        </v-progress-linear>
                        <div class="text-caption text-grey mt-1">
                            {{ importIndeterminate ? 'Merging on server...' : `Uploading... ${importProgress}%` }}
                        </div>
                    </div>

                    <v-alert v-if="importStatusMessage" :type="importStatusIsError ? 'error' : 'success'"
                        density="compact" variant="tonal">
                        {{ importStatusMessage }}
                    </v-alert>
                </v-card-text>
                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn variant="text" @click="showImportDialog = false">Close</v-btn>
                    <v-btn color="primary" variant="flat" :loading="importing" :disabled="!importFile"
                        @click="submitImport">
                        Import
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <v-tabs v-if="view === 'personalBest' && availablePersonalBestSizes.length > 0"
            v-model="personalBestPartySize" density="compact" color="primary" class="mb-2">
            <v-tab v-for="size in availablePersonalBestSizes" :key="size" :value="size">
                {{ size }}{{ size === 1 ? ' (Solo)' : '' }}
            </v-tab>
        </v-tabs>

        <v-tabs v-if="view === 'arcana' && availableArcanaPartySizes.length > 0"
            v-model="arcanaPartySize" density="compact" color="primary" class="mb-2">
            <v-tab v-for="size in availableArcanaPartySizes" :key="size" :value="size">
                {{ size }}{{ size === 1 ? ' (Solo)' : '' }}
            </v-tab>
        </v-tabs>

        <v-tabs v-if="view === 'byBoss' && availableByBossPartySizes.length > 0"
            v-model="byBossPartySize" density="compact" color="primary" class="mb-2">
            <v-tab v-for="size in availableByBossPartySizes" :key="size" :value="size">
                {{ size }}{{ size === 1 ? ' (Solo)' : '' }}
            </v-tab>
        </v-tabs>

        <v-progress-linear v-if="loading" indeterminate color="primary" class="mb-2" />

        <v-alert v-if="error" type="error" density="compact" class="mb-4">{{ error }}</v-alert>

        <div v-else-if="bossOptions.length === 0" class="text-center text-grey pa-8">
            No tracked bosses configured. Add named bosses to track under the Config tab to populate the leaderboard -
            this keeps rankings limited to real encounters instead of stray trash mobs.
        </div>

        <div v-else-if="!loading && entries.length === 0" class="text-center text-grey pa-8">
            No finalized fights against {{ selectedBossLabel }} yet. Rankings appear here once a fight completes (idle ~{{ Math.round(fightGapThresholdSec) }}s
            after the last hit, or the boss dies) - existing logs are aggregated automatically in the background too.
        </div>

        <div v-else-if="filteredEntries.length === 0" class="text-center text-grey pa-8">
            No players match "{{ nameFilter }}".
        </div>

        <template v-else>
            <!-- By Arcana: one table per class, rather than one flat table
                 with an Arcana column - each already ranks players separated
                 by exact party size and deduplicated to their best fight
                 (see handleLeaderboardArcana). -->
            <template v-if="view === 'arcana'">
                <div v-for="group in arcanaGroups" :key="group.arcana" class="mb-6">
                    <div class="text-subtitle-1 font-weight-bold mb-2">{{ group.arcana }}</div>
                    <v-data-table
                        :headers="arcanaHeaders"
                        :items="group.entries"
                        items-per-page="-1"
                        density="compact"
                        class="bg-transparent leaderboard-table"
                        hide-default-footer>
                        <template v-slot:item.effectiveDps="{ item }">
                            <span class="font-weight-bold">{{ formatNumber(item.effectiveDps) }}</span>
                        </template>
                        <template v-slot:item.dps="{ item }">
                            {{ formatNumber(item.dps) }}
                        </template>
                        <template v-slot:item.activeDps="{ item }">
                            {{ formatNumber(item.activeDps) }}
                        </template>
                        <template v-slot:item.totalDamage="{ item }">
                            {{ formatNumber(item.totalDamage) }}
                        </template>
                        <template v-slot:item.startedAt="{ item }">
                            <span class="text-caption text-grey-lighten-1">{{ formatWhen(item.startedAt) }}</span>
                        </template>
                        <template v-slot:item.sessionTable="{ item }">
                            <span class="text-caption text-grey-lighten-1">{{ item.sessionTable }}</span>
                        </template>
                    </v-data-table>
                </div>
            </template>

            <v-data-table
                v-else
                :headers="headers"
                :items="filteredEntries"
                :sort-by="defaultSortBy"
                items-per-page="-1"
                density="compact"
                class="bg-transparent leaderboard-table"
                hide-default-footer>
                <template v-slot:item.arcana="{ item }">
                    {{ item.arcana || '-' }}
                </template>
                <template v-slot:item.effectiveDps="{ item }">
                    <span class="font-weight-bold">{{ formatNumber(item.effectiveDps) }}</span>
                </template>
                <template v-slot:item.dps="{ item }">
                    {{ formatNumber(item.dps) }}
                </template>
                <template v-slot:item.activeDps="{ item }">
                    {{ formatNumber(item.activeDps) }}
                </template>
                <template v-slot:item.totalDamage="{ item }">
                    {{ formatNumber(item.totalDamage) }}
                </template>
                <template v-slot:item.startedAt="{ item }">
                    <span class="text-caption text-grey-lighten-1">{{ formatWhen(item.startedAt) }}</span>
                </template>
                <template v-slot:item.sessionTable="{ item }">
                    <span class="text-caption text-grey-lighten-1">{{ item.sessionTable }}</span>
                </template>
            </v-data-table>
        </template>
    </v-sheet>
</template>

<script lang="ts">
import { defineComponent, ref, computed, watch, onMounted, inject, Ref } from 'vue';
import { formatNumber } from '@/util';
import { trackedBosses, currentUser } from '@/store';

interface LeaderboardEntry {
    rank: number;
    playerKey: string;
    displayName: string;
    arcana?: string;
    partySize?: number;
    bossName?: string;
    fightId: number;
    sessionTable: string;
    startedAt: number;
    totalDamage: number;
    dps: number;
    activeDps: number;
    effectiveDps: number;
    runCount?: number;
}

const ENDPOINTS: Record<string, string> = {
    arcana: '/api/leaderboard/arcana',
    byBoss: '/api/leaderboard/party-size',
    personalBest: '/api/leaderboard/personal-best',
};

export default defineComponent({
    name: 'Leaderboard',
    setup() {
        const raceNameMap = inject('raceNameMap') as Ref<Record<number, string>>;

        const view = ref<'arcana' | 'byBoss' | 'personalBest'>('arcana');
        const entries = ref<LeaderboardEntry[]>([]);
        const loading = ref(false);
        const error = ref('');
        const fightGapThresholdSec = ref(45);
        const nameFilter = ref('');

        const bossOptions = computed(() =>
            trackedBosses.value
                .map(raceId => ({
                    raceId,
                    label: raceNameMap?.value?.[raceId] || `Unknown (${raceId})`,
                }))
                .sort((a, b) => a.label.localeCompare(b.label))
        );
        const selectedBoss = ref<number | null>(null);
        const selectedBossLabel = computed(() =>
            bossOptions.value.find(b => b.raceId === selectedBoss.value)?.label || 'this boss'
        );

        // Personal Bests are ranked per exact party size server-side (see
        // handleLeaderboardPersonalBest) - a 3-person and a 4-person best
        // aren't comparable numbers - so the UI separates them into tabs,
        // built from whichever exact sizes actually came back in this
        // response rather than a fixed set of categories.
        const personalBestPartySize = ref<number | null>(null);

        const availablePersonalBestSizes = computed(() => {
            if (view.value !== 'personalBest') return [];
            const present = new Set(entries.value.map(e => e.partySize).filter((n): n is number => n != null));
            return Array.from(present).sort((a, b) => a - b);
        });

        watch(availablePersonalBestSizes, (sizes) => {
            if (sizes.length > 0 && !sizes.includes(personalBestPartySize.value as number)) {
                personalBestPartySize.value = sizes[0];
            }
        }, { immediate: true });

        // By Arcana is likewise split into party-size sub-tabs (mirroring
        // Personal Bests, above) - a solo run and a full-party run aren't
        // comparable DPS numbers, and each class's own ranking is already
        // scoped to exact party size server-side (handleLeaderboardArcana).
        const arcanaPartySize = ref<number | null>(null);

        const availableArcanaPartySizes = computed(() => {
            if (view.value !== 'arcana') return [];
            const present = new Set(entries.value.map(e => e.partySize).filter((n): n is number => n != null));
            return Array.from(present).sort((a, b) => a - b);
        });

        watch(availableArcanaPartySizes, (sizes) => {
            if (sizes.length > 0 && !sizes.includes(arcanaPartySize.value as number)) {
                arcanaPartySize.value = sizes[0];
            }
        }, { immediate: true });

        // By Boss is the plain, class-agnostic ranking for the selected
        // tracked boss (handleLeaderboardPartySize - every finalized fight,
        // not deduped per player), likewise split into party-size sub-tabs.
        const byBossPartySize = ref<number | null>(null);

        const availableByBossPartySizes = computed(() => {
            if (view.value !== 'byBoss') return [];
            const present = new Set(entries.value.map(e => e.partySize).filter((n): n is number => n != null));
            return Array.from(present).sort((a, b) => a - b);
        });

        watch(availableByBossPartySizes, (sizes) => {
            if (sizes.length > 0 && !sizes.includes(byBossPartySize.value as number)) {
                byBossPartySize.value = sizes[0];
            }
        }, { immediate: true });

        const filteredEntries = computed(() => {
            let list = entries.value;
            if (view.value === 'personalBest') {
                list = list.filter(e => e.partySize === personalBestPartySize.value);
            } else if (view.value === 'arcana') {
                list = list.filter(e => e.partySize === arcanaPartySize.value);
            } else if (view.value === 'byBoss') {
                list = list.filter(e => e.partySize === byBossPartySize.value);
            }
            const q = nameFilter.value?.trim().toLowerCase();
            if (!q) return list;
            return list.filter(e => e.displayName?.toLowerCase().includes(q));
        });

        // By Arcana renders one table per class instead of one flat table
        // with an Arcana column (see handleLeaderboardArcana - each class's
        // ranking is already scoped by exact party size and deduplicated to
        // each player's best fight), grouped from filteredEntries (already
        // scoped to the selected party-size tab) and sorted by rank.
        const arcanaGroups = computed(() => {
            if (view.value !== 'arcana') return [];
            const groups = new Map<string, LeaderboardEntry[]>();
            for (const e of filteredEntries.value) {
                const key = e.arcana || 'Unknown';
                if (!groups.has(key)) groups.set(key, []);
                groups.get(key)!.push(e);
            }
            return Array.from(groups.entries())
                .map(([arcana, list]) => ({
                    arcana,
                    entries: [...list].sort((a, b) => a.rank - b.rank),
                }))
                .sort((a, b) => a.arcana.localeCompare(b.arcana));
        });

        const arcanaHeaders = [
            { title: '#', key: 'rank', width: 56 },
            { title: 'Player', key: 'displayName' },
            { title: 'Avg DPS', key: 'dps', align: 'end' as const },
            { title: 'Active DPS', key: 'activeDps', align: 'end' as const },
            { title: 'Eff DPS', key: 'effectiveDps', align: 'end' as const },
            { title: 'Total Damage', key: 'totalDamage', align: 'end' as const },
            { title: 'When', key: 'startedAt' },
            { title: 'Session', key: 'sessionTable' },
        ];

        const headers = computed(() => {
            const cols: any[] = [
                { title: '#', key: 'rank', width: 56 },
            ];
            cols.push({ title: 'Player', key: 'displayName' });
            if (view.value === 'personalBest' || view.value === 'byBoss') {
                cols.push({ title: 'Arcana', key: 'arcana' });
            }
            if (view.value === 'personalBest') {
                cols.push({ title: 'Runs', key: 'runCount', align: 'end' });
            }
            cols.push(
                { title: 'Avg DPS', key: 'dps', align: 'end' },
                { title: 'Active DPS', key: 'activeDps', align: 'end' },
                { title: 'Eff DPS', key: 'effectiveDps', align: 'end' },
                { title: 'Total Damage', key: 'totalDamage', align: 'end' },
                { title: 'When', key: 'startedAt' },
                { title: 'Session', key: 'sessionTable' },
            );
            return cols;
        });

        const defaultSortBy = computed(() => [{ key: 'rank', order: 'asc' as const }]);

        const load = async () => {
            if (selectedBoss.value === null) {
                entries.value = [];
                return;
            }
            loading.value = true;
            error.value = '';
            try {
                const resp = await fetch(`${ENDPOINTS[view.value]}?bossRaceId=${selectedBoss.value}`);
                if (!resp.ok) {
                    throw new Error(`Request failed: ${resp.status}`);
                }
                const data = await resp.json();
                entries.value = data.entries || [];
            } catch (e: any) {
                error.value = e?.message || 'Failed to load leaderboard';
                entries.value = [];
            } finally {
                loading.value = false;
            }
        };

        function formatWhen(startedAt: number): string {
            if (!startedAt) return '-';
            return new Date(startedAt * 1000).toLocaleString([], {
                month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
            });
        }

        // --- Import Data (merge another user's leaderboard rows in) ---
        // Uploads a dpslog.db/leaderboard.db file in fixed-size chunks - the
        // server extracts only players/fights/fight_participants from it
        // (see handleImportLeaderboard) and discards the rest, so this
        // component never needs to know or care how large the source file
        // itself is. Chunking (rather than one request for the whole file)
        // matters because this app is often reached through a reverse proxy
        // - Cloudflare in particular hard-caps proxied request bodies at
        // 100MB - which would reset the connection for a multi-gigabyte
        // dpslog.db sent as a single request before it ever reached the
        // server.
        const IMPORT_CHUNK_SIZE = 40 * 1024 * 1024; // well under Cloudflare's 100MB proxied request cap

        function randomUploadId(): string {
            if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
                return crypto.randomUUID();
            }
            return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
        }

        function uploadImportChunk(
            uploadId: string, chunkIndex: number, totalChunks: number,
            filename: string, label: string, blob: Blob,
            onProgress: (loaded: number) => void,
        ): Promise<any> {
            return new Promise((resolve, reject) => {
                const form = new FormData();
                form.append('uploadId', uploadId);
                form.append('chunkIndex', String(chunkIndex));
                form.append('totalChunks', String(totalChunks));
                form.append('filename', filename);
                form.append('label', label);
                form.append('file', blob);

                const xhr = new XMLHttpRequest();
                xhr.open('POST', '/api/leaderboard/import');
                xhr.upload.onprogress = (e) => {
                    if (e.lengthComputable) onProgress(e.loaded);
                };
                xhr.onload = () => {
                    let data: any = {};
                    try { data = JSON.parse(xhr.responseText || '{}'); } catch { /* non-JSON error body */ }
                    if (xhr.status >= 200 && xhr.status < 300) {
                        resolve(data);
                    } else {
                        reject(new Error(data?.message || `Request failed: ${xhr.status}`));
                    }
                };
                xhr.onerror = () => reject(new Error('Network error during import.'));
                xhr.send(form);
            });
        }

        const showImportDialog = ref(false);
        const importFile = ref<File[] | File | null>(null);
        const importLabel = ref('');
        const importing = ref(false);
        const importStatusMessage = ref('');
        const importStatusIsError = ref(false);
        // Upload progress (0-100) across all chunks combined; once the last
        // chunk's bytes are fully sent, the server is still busy merging rows
        // (can take a moment for a big dpslog.db), so we switch to an
        // indeterminate bar rather than pretending we're done.
        const importProgress = ref(0);
        const importIndeterminate = ref(false);

        const resetImportForm = () => {
            importFile.value = null;
            importLabel.value = '';
            importStatusMessage.value = '';
            importStatusIsError.value = false;
            importProgress.value = 0;
            importIndeterminate.value = false;
        };

        const submitImport = async () => {
            const fileValue = importFile.value;
            const file = Array.isArray(fileValue) ? fileValue[0] : fileValue;
            if (!file) return;

            importing.value = true;
            importStatusMessage.value = '';
            importProgress.value = 0;
            importIndeterminate.value = false;

            const uploadId = randomUploadId();
            const totalChunks = Math.max(1, Math.ceil(file.size / IMPORT_CHUNK_SIZE));
            const label = importLabel.value;

            try {
                let bytesDone = 0;
                for (let i = 0; i < totalChunks; i++) {
                    const start = i * IMPORT_CHUNK_SIZE;
                    const end = Math.min(start + IMPORT_CHUNK_SIZE, file.size);
                    const chunk = file.slice(start, end);
                    const isLast = i === totalChunks - 1;
                    const chunkStartBytes = bytesDone;

                    const data = await uploadImportChunk(uploadId, i, totalChunks, file.name, label, chunk, (loaded) => {
                        importProgress.value = file.size > 0
                            ? Math.round(((chunkStartBytes + loaded) / file.size) * 100)
                            : 100;
                        if (isLast && loaded >= chunk.size) {
                            importIndeterminate.value = true;
                        }
                    });
                    bytesDone += chunk.size;

                    if (isLast) {
                        importIndeterminate.value = false;
                        importStatusIsError.value = false;
                        importStatusMessage.value = `Imported ${data.fightsImported ?? 0} fight(s), ${data.participantsImported ?? 0} participant row(s).`;
                        load();
                    }
                }
            } catch (e: any) {
                importStatusIsError.value = true;
                importStatusMessage.value = e?.message || 'Import failed.';
            } finally {
                importing.value = false;
                importIndeterminate.value = false;
            }
        };

        // Keep selectedBoss valid as the tracked-boss list (and race names) load
        // in, possibly after this component has already mounted.
        watch(bossOptions, (opts) => {
            if (!opts.some(o => o.raceId === selectedBoss.value)) {
                selectedBoss.value = opts.length > 0 ? opts[0].raceId : null;
            }
        }, { immediate: true });

        watch(view, load);
        watch(selectedBoss, load);
        onMounted(load);

        return {
            view, entries, loading, error, fightGapThresholdSec, nameFilter,
            bossOptions, selectedBoss, selectedBossLabel, filteredEntries,
            headers, defaultSortBy, arcanaGroups, arcanaHeaders,
            personalBestPartySize, availablePersonalBestSizes,
            arcanaPartySize, availableArcanaPartySizes,
            byBossPartySize, availableByBossPartySizes,
            currentUser,
            showImportDialog, importFile, importLabel, importing,
            importProgress, importIndeterminate,
            importStatusMessage, importStatusIsError, submitImport, resetImportForm,
            formatNumber, formatWhen, load,
        };
    },
});
</script>

<style scoped>
.leaderboard-table :deep(th) {
    text-transform: uppercase;
    font-size: 0.75rem;
    letter-spacing: 0.025rem;
    color: #bdbdbd !important;
}
</style>
